import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableChargerPointReport,
  RequestReportInterFace,
} from '../../../../interface/reportChargerPoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import {
  fetchChargerReportPoint,
  setReportFilterData,
  setReportPointFiltersData,
  updateReportFilters,
  fetchListFilters,
  setSortData,
  setChargerPointReportUpdate,
} from '../../../../redux/slice/ReportChargerPointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slice/ReportSlice';
import { RootState } from '../../../../redux/store';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import StationViewSidePanel from '../../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import reportApi from '../../../../utils/api/report';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter?: any;
  clearDateRange?: () => void;
}

/**
 * ChargerReportTablePoint component
 */
function ChargerReportTablePoint(properties: ReportTableProperties) {
  const { leftTopContent, preferenceFilter, clearDateRange } = properties;

  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
    isChargerPointReportUpdate,
  } = useAppSelector((state: RootState) => state.reportChargerPoint);
  const { currentFacilityTagsList } = useAppSelector((state: RootState) => state.facilityTags);
  const reportsData = useAppSelector((state: RootState) => state.reportData);

  const column = [
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailableChargerPointReport,
      sortAllow: false,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingPointName' as keyof AvailableChargerPointReport,
      /**
       * S.No view
       * @param station
       * @param index
       */
      customData: (report: AvailableChargerPointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="button" tabIndex={0}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="button" tabIndex={0}>
              {report.chargingStationLocation}
            </span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      sortAllow: false,
      columnWidth: 204,
    },
    {
      label: t('upTime'),
      accessorKey: 'uptime' as keyof AvailableChargerPointReport,
      sortAllow: true,
    },
    {
      label: t('downTime'),
      accessorKey: 'downtime' as keyof AvailableChargerPointReport,
      sortAllow: true,
    },

    {
      label: t('idle'),
      accessorKey: 'idle' as keyof AvailableChargerPointReport,
      sortAllow: true,
    },
    {
      label: t('inUse'),
      accessorKey: 'inuse' as keyof AvailableChargerPointReport,
      sortAllow: true,
    },
  ];

  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchChargerReportPoint({
        ...queryParameters,
        sortField,
        sortBy,
        page: pageCount,
        limit,
      }),
    );
  };

  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setReportFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    dispatch(
      fetchChargerReportPoint({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchChargerReportPoint({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit: limitCount,
      }),
    );
  };
  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchChargerReportPoint({
        ...queryParameters,
        sortBy,
        sortField,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page,
        limit,
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('chargingPoint'), accessorKey: 'chargingPointSn' },
    { label: t('stationInfo'), accessorKey: 'chargingPointName' },
    { label: t('upTime'), accessorKey: 'uptime' },
    { label: t('downTime'), accessorKey: 'downtime' },
    { label: t('idle'), accessorKey: 'idle' },
    { label: t('inUse'), accessorKey: 'inuse' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    const exportParameters = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    };
    reportApi
      .fetchChargerPointListExport({ ...exportParameters })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: AvailableChargerPointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'chargingPointName':
                return `${row.chargingStationName} ${ConvertUTCtoDate(row.date)} ${row?.chargingStationLocation ?? ''}`;
              case 'uptime':
                return row?.uptime ?? 0;
              case 'downtime':
                return row?.downtime ?? 0;
              case 'idle':
                return row?.idle ?? 0;
              case 'inuse':
                return row?.inuse ?? 0;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${t('reportsList')}.csv`);
          } else {
            exportPDF(headers, rows, `${t('reportsList')}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  /**
   * Fetches energy report data for the selected facility.
   * @param data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchChargerReportPoint({
          ...data,
        }),
      );
    }
  };
  const fetchReportData = useCallback(() => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);
    if (!selectedFacility?._id) return;

    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchChargerReportPoint({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      }),
    );
  }, [
    currentFacilityTagsList,
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isChargerPointReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setChargerPointReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isChargerPointReportUpdate]);

  useEffect(() => {
    if (filters.length === 1) {
      dispatch(fetchListFilters());
    }
    if (
      preferenceFilter.pageName === 'chargerUtilizationReport' &&
      reportsData.reportByType !== 'station'
    ) {
      if (filters.length > 1) {
        const setFilter = filters.map((filter) => {
          let returnValue = filter;
          const single = preferenceFilter.filter?.find(
            (preference: any) => preference.field === filter.key,
          );
          if (single && single?.value.length > 0) {
            returnValue = {
              ...returnValue,
              value: single?.value,
              applyFilter: true,
              valueToShow: returnValue?.listOfValue
                ?.filter((show) => single?.value?.includes(show.value))
                .map((show) => show.label),
            };
          } else {
            returnValue = {
              ...returnValue,
              value: [],
              applyFilter: false,
              valueToShow: [],
            };
          }
          return returnValue;
        });
        dispatch(setReportPointFiltersData(setFilter));
        const queryParameters = GetParametersFromCommonFilters(setFilter);
        fetchData({
          ...queryParameters,
          startDate: reportsData.reportFilter.startDate,
          endDate: reportsData.reportFilter.endDate,
          page: DefaultApiLimit.page,
          limit,
        });
      }
    }
  }, [preferenceFilter]);

  return (
    <div>
      <CustomTable
        paginate
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        tableHeight="calc(100vh - 335px)"
        version="v2"
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        onHiddenColumnsChange={setHiddenColumns}
        exportData={reports}
        exportLoader={exportLoader}
        topHeader={false}
        filters={filters}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        rightTopContent={leftTopContent}
        refreshCurrentPages={ReferenceCurrentPages}
        serialNo
        onClearFilterChange={() => {
          if (clearDateRange) {
            clearDateRange();
          }
        }}
        flexDirection
      />
    </div>
  );
}

export default ChargerReportTablePoint;
